import { ArenaFinancialConfig, CourtCustomFinancialConfig, ArenaFinancialModelType, DepositConfig } from "./commercialRules";

export type UserAccountType = 'atleta' | 'dono_arena';

export const OFFICIAL_ADMIN_EMAILS = [
  "todossaodigitais@gmail.com",
  "admin@hubesportivo.com",
  "admin@suporte.com"
];

export type ArenaVerificationStatus = 'nao_solicitada' | 'pendente' | 'verificada';

export interface ArenaVerificationData {
  cnpj?: string;
  legalName?: string;
  address?: string;
  status: ArenaVerificationStatus;
  verifiedAt?: number;
}

export interface LessonStudentPaymentRecord {
  id: string;
  competence: string; // "YYYY-MM" (ex: "2026-09")
  amount: number;
  paidAt: string; // ISO string ou "YYYY-MM-DD"
  recordedBy?: string;
  notes?: string;
  createdAt: string;
}

export type LessonBillingStatus = "PENDING" | "PAID" | "PARTIAL" | "OVERDUE" | "EXEMPT";

export interface LessonStudent {
  id: string;
  athleteId?: string;
  athleteUid?: string;
  userId?: string;
  name: string;
  phone?: string;
  email?: string;
  cpf?: string;
  monthlyFee?: number;
  status?: 'active' | 'inactive';
  joinedAt?: string;
  notes?: string;
  isExempt?: boolean;
  exemptReason?: string;
  exemptCompetences?: string[]; // competências específicas isentas (ex: ["2026-09"])
  paymentHistory?: LessonStudentPaymentRecord[];
}

export interface FixedSchedule {
  id: string;
  dayOfWeek?: string; // ex: "Segunda-feira"
  weekday?: string; // ex: "Segunda-feira"
  time?: string; // ex: "09:00"
  startTime?: string; // ex: "19:00"
  endTime?: string; // ex: "20:00"
  athleteName: string;
  athleteCpf?: string;
  athletePhone?: string;
  coachName?: string;
  students?: LessonStudent[];
  maxStudents?: number;
  notes?: string;
  dueDateDay?: number; // Dia do mês do vencimento da mensalidade (ex: 10)
  monthlyFeeAmount?: number; // Valor mensal (ex: 280)
  monthlyFee?: number;
  paymentStatus?: 'em_dia' | 'pendente' | 'vencido';
  lastPaidMonth?: string; // YYYY-MM
  scheduleType?: 'mensalista' | 'aula'; // Horário Fixo Mensalista ou Aula/Treino
  athleteId?: string;
  lastCheckInDate?: string;
  lastAttendanceStatus?: 'PRESENTE' | 'AUSENTE' | 'PENDENTE' | string;
  lastValidatedAt?: string;
  attendanceHistory?: Array<{
    date: string;
    status: string;
    validatedAt: string;
    validatedBy?: string;
  }>;
  payDeadlineHours?: number; // Ex: 24h antes do jogo para pagar a entrada ou perde o horário fixo
  status?: 'ACTIVE' | 'SUSPENDED' | 'LOST' | 'PAUSED' | 'em_dia' | 'inativo';
  hourlyRate?: number; // Valor da hora avulsa/utilizada do horário fixo (ex: R$ 20)
  penaltyLimit?: number; // Limite de penalizações para perda do horário fixo (padrão 2)
  penaltyCount?: number; // Contador acumulado de penalizações do horário fixo
  penaltiesHistory?: Array<{
    occurrenceId: string;
    date: string;
    type: 'UNPAID_EXPIRED' | 'NO_SHOW' | 'LATE_CANCELLATION' | 'ARENA_MANUAL';
    reason: string;
    appliedAt: string;
    appliedBy: string;
  }>;
  totalAbsences?: number; // Contador oficial de faltas/no-shows (2 faltas = perda do horário)
  absenceDates?: string[]; // Histórico auditável de datas de faltas
  lastAbsenceDate?: string; // Data da última falta registrada
  lastAbsenceReason?: string; // Motivo registrado da falta
  lostAt?: string; // Timestamp do momento exato da perda do horário
  lossReason?: string; // Motivo da perda (ex: 2 faltas consecutivas)
}

export interface BookingAddonItem {
  id: string;
  name: string;
  price: number;
  category: 'equipamento' | 'bebida' | 'snack' | 'outro';
  quantity?: number;
}

export interface OpenMatchPlayer {
  id: string;
  name: string;
  phone: string;
  cpf?: string;
  joinedAt: number;
  paidShare: number;
}

export interface LoyaltyProfile {
  athleteCpf: string;
  athleteName: string;
  points: number;
  tier: 'Bronze' | 'Prata' | 'Ouro' | 'Diamante';
  freeHoursAvailable: number;
  totalBookingsCompleted: number;
}

export interface BlockedCpfItem {
  id: string;
  cpf: string;
  athleteName: string;
  reason: string;
  blockedAt: number;
  arenaName?: string; // Nome da arena que efetuou o bloqueio
}

export interface CourtReview {
  id: string;
  courtId: string;
  arenaName: string;
  athleteName: string;
  rating: number; // 1 a 5
  comment: string;
  createdAt: number;
  tags?: string[]; // ex: ["Areia Premium", "Boa Iluminação", "Estacionamento Fácil"]
}

export interface WaitlistEntry {
  id: string;
  courtId: string;
  courtName: string;
  arenaName: string;
  date: string;
  time: string;
  athleteName: string;
  athletePhone: string;
  athleteCpf: string;
  createdAt: number;
}

export interface RegisteredAthleteItem {
  id: string;
  cpf: string;
  athleteName: string;
  phone?: string;
  email?: string;
  status: 'ativo' | 'mensalista' | 'bloqueado';
  notes?: string;
  registeredAt: number;
}

export interface UserProfile {
  id: string;
  uid?: string;
  name: string;
  email: string;
  cpf?: string; // CPF obrigatório para identificação de atletas
  cnpj?: string; // CNPJ para empresas e donos de arena
  birthDate?: string; // Data de nascimento do atleta
  phone?: string;
  avatarUrl?: string;
  pixKey?: string;
  pixKeyType?: "CPF" | "CNPJ" | "PHONE" | "EMAIL" | "EVP" | "cpf" | "cnpj" | "email" | "phone" | "random" | string;
  pixAddressKey?: string;
  pixAddressKeyType?: "CPF" | "CNPJ" | "PHONE" | "EMAIL" | "EVP" | string;
  pixKeyValidated?: boolean;
  pixKeyValidatedAt?: string | number;
  pixKeyValidationMessage?: string;
  ownerPixKey?: string;
  ownerPixKeyType?: "CPF" | "CNPJ" | "PHONE" | "EMAIL" | "EVP" | string;
  ownerPixKeyValidated?: boolean;
  ownerPixKeyValidatedAt?: string | number;
  accountType: UserAccountType; // 'atleta' ou 'dono_arena'
  role?: string; // 'admin' ou 'atleta' ou 'dono_arena'
  isSuperAdmin?: boolean;
  subscriptionStatus?: 'ACTIVE' | 'OVERDUE' | 'SUSPENDED' | 'CANCELLED' | string;
  operationalStatus?: 'ACTIVE' | 'PENDING' | 'SUSPENDED' | 'BLOCKED' | string;
  paymentStatus?: 'em_dia' | 'inadimplente' | 'pendente_5dias' | string;
  verificationStatus?: 'PENDING' | 'VERIFIED' | 'BLOCKED' | 'SUSPENDED' | string;
  trialStartAt?: number;
  trialEndAt?: number;
  trialStatus?: 'TRIAL_ACTIVE' | 'TRIAL_EXPIRED' | 'OPERATION_ACTIVE' | 'PDV_ACTIVE' | 'PDV_EXPIRED';
  commercialStatus?: 'TRIAL_ACTIVE' | 'TRIAL_EXPIRED' | 'OPERATION_ACTIVE' | 'PDV_ACTIVE' | 'PDV_EXPIRED';
  commercialPlanId?: string;
  commercialPlanName?: string;
  isOperationOnly?: boolean;
  isPDVPlan?: boolean;
  financialModel?: ArenaFinancialModelType;
  city?: string;
  state?: string;
  preferredSports?: string[];
  createdAt: number;
  updatedAt?: number;
  arenaVerification?: ArenaVerificationData;
  blockedCpfs?: BlockedCpfItem[];
  registeredAthletes?: RegisteredAthleteItem[];
  // Campos específicos do Dono de Arena
  arenaId?: string;
  arenaName?: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood?: string; // Bairro
  sector?: string; // Setor
  zipCode?: string; // CEP
  whatsappContact?: string; // WhatsApp de atendimento da arena para os atletas
  enableWhatsappContact?: boolean; // Permite exibição pública do WhatsApp para atletas
  financialConfig?: ArenaFinancialConfig; // Configuração do Modelo Financeiro da Arena (SPLIT / MENSALIDADE FIXA / HÍBRIDO)
  arenaFinancialModel?: ArenaFinancialModelType;
  arenaCommissionModel?: ArenaFinancialModelType;
  arenaImageUrl?: string;
  arenaLogoUrl?: string;
}

export interface CourtItem {
  id: string;
  arenaId?: string;
  canonicalArenaId?: string;
  arenaName: string;
  courtName: string;
  userId?: string;
  ownerUid?: string;
  arenaFinancialModel?: ArenaFinancialModelType;
  status?: string;
  isActive?: boolean;
  active?: boolean;
  sports: string[]; // Múltiplas modalidades (ex: Beach Tennis, Futevôlei)
  pricePerHour: number;
  location: string; // Endereço formatado
  street: string; // Rua / Número / Logradouro
  neighborhood: string; // Bairro
  city: string; // Cidade
  state: string; // Estado / UF (ex: SP, RJ, MG)
  availableDays: string[]; // ex: ["Segunda-feira", "Terça-feira", ...]
  openingTime: string; // ex: "06:00"
  closingTime: string; // ex: "23:00"
  isVerifiedArena?: boolean;
  isPremiumArena?: boolean;
  verificationStatus?: "PENDING_APPROVAL" | "UNDER_REVIEW" | "VERIFIED" | "SUSPENDED" | "BLOCKED";
  verificationRejectionReason?: string;
  verificationNotes?: string;
  latitude?: number;
  longitude?: number;
  facadePhotoUrl?: string;
  courtPhotoUrl?: string;
  receptionPhotoUrl?: string;
  parkingPhotoUrl?: string;
  hasParking?: boolean;
  ratingStructure?: number;
  ratingCleanliness?: number;
  ratingService?: number;
  ratingLighting?: number;
  ratingOrganization?: number;
  reviewsCount?: number;
  activeReportsCount?: number;
  ownerId?: string;
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  ownerCpfCnpj?: string;
  ownerWhatsapp?: string;
  enableOwnerWhatsapp?: boolean;
  rating?: number;
  imageUrl?: string; // Foto da Quadra / Arena
  arenaImageUrl?: string; // Imagem de Capa da Arena
  arenaLogoUrl?: string; // Logo da Arena
  isMaintenance?: boolean; // Quadra em manutenção ou fechada temporariamente
  isDayUseActive?: boolean; // Se a quadra é de uso livre / Day Use
  dayUsePrice?: number; // Preço do Day Use por pessoa
  dayUseNotes?: string;
  cancellationHours?: number; // Prazo limite em horas para cancelamento (ex: 24h)
  advancePaymentPercentage?: number; // Porcentagem do sinal/entrada (ex: 50%)
  slotIntervalMinutes?: 15 | 30 | 60; // Intervalo da grade (15m, 30m ou 60m)
  ownerPixKey?: string; // Chave Pix específica da arena
  ownerPixKeyType?: "cpf" | "cnpj" | "email" | "phone" | "random" | "EVP" | "CPF" | "CNPJ" | "EMAIL" | "PHONE" | string;
  pixKey?: string;
  pixKeyType?: "cpf" | "cnpj" | "email" | "phone" | "random" | "EVP" | "CPF" | "CNPJ" | "EMAIL" | "PHONE" | string;
  ownerMercadoPagoAccessToken?: string; // Access Token de Produção/Sandbox do Mercado Pago da Arena
  ownerMercadoPagoPublicKey?: string; // Public Key do Mercado Pago da Arena
  ownerMercadoPagoConnected?: boolean; // Se a arena conectou/vinculou sua conta Mercado Pago
  chargeBookings?: boolean; // Se ativado (true), cobra agendamentos via Pix; se desativado (false), modo teste/grátis
  paymentDestination?: "direto_master" | "direto_arena" | "padrao_sistema"; // Sobrescreve destino do Pix da quadra/arena
  billingType?: "pix_direto" | "mensalidade_arena" | "mercado_pago_split" | "padrao_sistema"; // Sobrescreve modelo de cobrança
  customCommissionRate?: number; // Taxa % de comissão customizada específica desta quadra
  customDayUseCommissionRate?: number; // Taxa % de comissão de Day Use customizada específica desta quadra
  useCustomFinancialConfig?: boolean; // Se ativado, usa regras comerciais customizadas para esta quadra
  customFinancialConfig?: CourtCustomFinancialConfig; // Modelo comercial customizado da quadra (Court > Arena > Platform)
  paymentStatus?: 'em_dia' | 'inadimplente' | 'pendente_5dias' | string; // Status da mensalidade
  cancellationWindowHours?: number; // Janela de cancelamento da quadra (2, 4 ou 6 horas)
  fixedSchedules?: FixedSchedule[]; // Horários fixos semanalmente para atletas mensalistas
  slots: CourtSlot[];
}

export interface CourtSlot {
  id: string;
  courtId: string;
  courtName: string;
  sport: string;
  city: string;
  neighborhood: string;
  date: string; // YYYY-MM-DD
  time: string; // HH:MM
  endTime?: string; // HH:MM
  price: number;
  slotType?: 'padrao' | 'familia' | 'day_use' | 'torneio_gratuito'; // 'familia' = cortesia; 'day_use' = uso livre; 'torneio_gratuito' = torneio/evento sem comissão
  dayUsePrice?: number; // Valor específico do Day Use
  depositAmount?: number;
  customerName?: string;
  status: 'liberada' | 'marcada' | 'pendente' | 'cancelada' | 'manutencao' | 'horario_fixo' | 'no_show';
  arenaName: string;
  arenaImageUrl?: string;
  bookedBy?: string;
  bookedPhone?: string;
  bookedCpf?: string;
  bookingId?: string;
  fixedAthleteName?: string;
  fixedAthleteCpf?: string;
  ticketCode?: string; // Código de check-in / QR Code (ex: "TKT-9842")
  checkedIn?: boolean;
  checkedInAt?: number;
  isNoShow?: boolean;
  noShowAt?: string;
  noShowReason?: string;
  addons?: BookingAddonItem[]; // Itens de comanda/aluguel adicionados
  isOpenMatch?: boolean; // Vaga aberta "Procura-se Jogador / Chamado"
  maxOpenMatchPlayers?: number; // Total de vagas abertas (ex: 4 pessoas)
  openMatchPlayers?: OpenMatchPlayer[]; // Jogadores que já entraram no horário
  openMatchCostPerPlayer?: number; // Valor individual por jogador
  matchCalloutNotes?: string; // Mensagem do chamado ("Preciso de 2 jogadores nível intermediário")
  matchCalloutPhone?: string; // WhatsApp do organizador da partida
  cancellationDeadlineHours?: number; // Horas de antecedência para cancelar
  cancellationWindowHours?: number; // Janela de cancelamento (2, 4, 6 horas) congelada
  appliedCancellationDeadlineAt?: string; // Data/Hora exata limite ISO congelada
  createdAt?: string; // Data/Hora ISO em que a reserva foi criada
  cancelledAt?: string; // Data/Hora ISO em que a reserva foi cancelada
  isCancelled?: boolean; // Flag indicando cancelamento
  cancellationType?: string;
  refundRecord?: any;
  refundEligible?: boolean;
  isMensalista?: boolean;
  grossAmount?: number;
  entryFee?: number;
  totalAmount?: number;
  commissionRate?: number; // Taxa % de comissão congelada da reserva
  financialModel?: string;
  financialModelApplied?: "FIXED_MONTHLY" | "SPLIT" | "HYBRID" | string;
  financialSnapshot?: import("./commercialRules").ArenaOperationFinancialSnapshot;
  financialModelSnapshot?: import("./commercialRules").ArenaOperationFinancialSnapshot;
  // Suporte a Agendamento de Torneio vinculado ao Organizador
  isTournamentSlot?: boolean;
  linkedTournamentId?: string;
  linkedTournamentRequestId?: string;
  organizerName?: string;
  organizerCpf?: string;
  organizerEmail?: string;
  organizerPhone?: string;
  pendingTournamentLink?: boolean; // Se true, aguarda a criação do torneio pelo organizador com este CPF/Email

  // Suporte a Financeiro e Comprovante Pix Mercado Pago
  paymentId?: string;
  paymentStatus?: 'pago' | 'pendente' | 'cancelado' | 'nao_aplicavel';
  paidAmount?: number;
  advancePercentage?: number;
  advanceAmount?: number;
  appCommission?: number; // Comissão em R$ da plataforma (4%)
  mpFeeAmount?: number; // Taxa do MP (0.99%)
  whoPaysMpFee?: 'platform' | 'arena'; // Quem pagou a taxa do MP
  arenaNetFromAdvance?: number; // Valor líquido creditado para a arena
  remainingBalanceAtArena?: number; // Saldo a pagar presencialmente
  paidAt?: number;
  ownerPixKey?: string;
  ownerPixKeyType?: "cpf" | "cnpj" | "email" | "phone" | "random" | "EVP" | "CPF" | "CNPJ" | "EMAIL" | "PHONE" | string;

  // Bloco C - Controle Autoritativo de Check-in e Pagamento Presencial do Restante
  checkInStatus?: "PENDENTE" | "UTILIZADO" | "CANCELADO";
  checkInAt?: string;
  validatedBy?: string;
  remainingAmount?: number;
  remainingPaidAmount?: number;
  remainingPaymentStatus?: "PENDENTE" | "PAGO" | "ISENTO" | "CANCELADO";
  remainingPaymentMethod?: "dinheiro" | "pix_balcao" | "pix_maquininha" | "cartao_credito_balcao" | "cartao_debito_balcao" | "outros";
  remainingPaidAt?: string;
  remainingConfirmedBy?: string;
  remainingNotes?: string;
}

export interface ArenaCategory {
  id: string;
  name: string;
  ownerId?: string;
  arenaId?: string;
  userId?: string;
  arenaOwnerId?: string;
  ownerEmail?: string;
  email?: string;
  createdAt?: number;
  updatedAt?: number;
}

export interface ArenaProduct {
  id: string;
  name: string;
  category: string;
  price: number; // Preço de venda
  costPrice?: number; // Preço de custo
  profitMarginPercent?: number; // Margem de lucro desejada %
  stockQuantity: number;
  minStockThreshold?: number; // Alerta de estoque mínimo
  createdAt?: number;
  updatedAt?: number;
  ownerId?: string;
  userId?: string;
  arenaOwnerId?: string;
  ownerEmail?: string;
  email?: string;
  arenaId?: string;
}

export interface ArenaSaleItem {
  productId: string;
  productName: string;
  unitPrice: number;
  unitCostPrice?: number; // Custo unitário no momento da venda
  quantity: number;
  subtotal?: number;
}

export interface ArenaSaleTransaction {
  id: string;
  customerName: string;
  customerPhone?: string;
  customerCpf?: string;
  items: ArenaSaleItem[];
  totalAmount: number;
  status: 'pago' | 'pendente';
  paymentStatus?: string;
  paymentMethod: 'Pix' | 'Dinheiro' | 'Cartão' | 'Fiado / Comanda';
  createdAt: number;
  updatedAt?: number;
  paidAt?: number;
  date?: string;
  time?: string;
  notes?: string;
  ownerId?: string;
  userId?: string;
  arenaOwnerId?: string;
  ownerEmail?: string;
  email?: string;
  arenaId?: string;
  isDirectPix?: boolean; // Pix presencial/direto do PDV (isento de taxa de gateway online)
  // Campos de Maquininha de Cartão de Balcão/PDV
  cardMachineId?: string;
  cardMachineName?: string;
  cardModality?: 'debito' | 'credito_vista' | 'credito_parcelado' | string;
  cardInstallments?: number;
  cardFeeRatePercent?: number;
  cardFeeAmount?: number;
  netAmount?: number;
}

export interface ArenaCardMachine {
  id: string;
  ownerId?: string;
  arenaId?: string;
  name: string; // Ex: Stone 01, PagBank Arena
  acquirer?: string;
  serialNumber?: string;
  pixFeePercent?: number; // Ex: 0.49 (%) Taxa de PIX na maquininha
  debitFeePercent?: number; // Ex: 1.99 (%)
  creditSightFeePercent?: number; // Ex: 3.49 (%)
  installmentFees?: Record<string | number, number>; // { 2: 4.29, 3: 4.79, ... }
  fixedFeePerTransaction?: number;
  maxInstallments?: number;
  isDefault: boolean;
  active: boolean;
  availableForCourtCheckin?: boolean; // Disponível para recebimentos presenciais no Check-in das Quadras (padrão !== false)
  notes?: string;
  createdAt?: number;
  updatedAt?: number;
}

export type ArenaOperationalExpenseCategory =
  | 'aluguel'
  | 'energia_agua'
  | 'folha_pagamento'
  | 'manutencao'
  | 'limpeza'
  | 'internet_telefonia'
  | 'marketing'
  | 'impostos_taxas'
  | 'material_esportivo'
  | 'alimentacao_bar'
  | 'outros';

export interface ArenaOperationalExpense {
  id: string;
  arenaId?: string;
  ownerId?: string;
  arenaOwnerId?: string;
  userId?: string;
  ownerEmail?: string;
  email?: string;
  description: string;
  amount: number;
  category: ArenaOperationalExpenseCategory;
  supplier?: string;
  paymentMethod: 'pix' | 'dinheiro' | 'cartao_debito' | 'cartao_credito' | 'boleto' | 'transferencia' | string;
  date: string; // YYYY-MM-DD
  dueDate?: string; // YYYY-MM-DD
  status: 'pago' | 'pendente' | 'cancelado';
  receiptUrl?: string;
  notes?: string;
  createdAt: number;
  updatedAt?: number;
}


